import Dexie, { type Table } from "dexie";
import type {
  DRBackup,
  DRLocalDrawingData,
  DRImageRecord,
  DRDrawing,
  DRPartialProject,
} from "@/types";
import type { LegendDwgCache } from "@/types/legend.types";
import type { DRActivityEvent } from "@/types/activity-event.types";
import type { DRPlanAnalysis } from "@/types/plan-analysis.types";
import { Logger } from "@/logger";
import i18next from "i18next";

type DATABASE_TABLE_NAMES =
  | "drawings" // Table of DRLocalDrawingData (local used data for drawing tool: drafts, dwg, etc)
  | "files" // Table of DRDrawing (same as server table 'drawings')
  | "projects"
  | "backups"
  | "drawingImages"
  | "legendDwgFiles" // DWG files cached per legend
  | "activityEvents" // Oplog events (offline) — cf. activity-event.types
  | "planAnalyses"; // Analyse du plan par dessin (groupes détectés + statuts)

export class DRAppDatabase extends Dexie {
  static dbId = "DRAppDB";
  readonly dbVersion = 17;
  public drawings!: Table<DRLocalDrawingData, number>;
  public files!: Table<DRDrawing, number>;
  public projects!: Table<DRPartialProject, number>;
  public backups!: Table<DRBackup, number>;
  public drawingImages!: Table<DRImageRecord, number>;
  public legendDwgFiles!: Table<LegendDwgCache, number>;
  public activityEvents!: Table<DRActivityEvent, string>;
  public planAnalyses!: Table<DRPlanAnalysis, number>;

  public constructor() {
    super(DRAppDatabase.dbId);

    const stores: Record<DATABASE_TABLE_NAMES, string> = {
      drawings: "id",
      projects: "id, status, name, createdAt, organizationId",
      files: "id, projectId, organizationId",
      backups: "++id, drawingId, revisionId, userId",
      drawingImages: "id, rasterPath, drawingId",
      legendDwgFiles: "id",
      activityEvents: "eventId, drawingId, entityUid, revisionId, synced",
      planAnalyses: "drawingId",
    };
    this.version(this.dbVersion).stores(stores);

    this.on("ready", () => {
      Logger.debug("database.ts : Db ready");
    });
    this.on("blocked", () => {
      alert(
        "Un autre onglet est déjà ouvert avec une autre version du site. Sauvegardez les données et fermez avant de rafraichir la page"
      );
    });
    this.on("close", () => {
      Logger.warn("database.ts : db closed");
      alert(
        `Nous avons eu un problème. Cela arrive parfois lorsqu'une page reste ouverte longtemps. Pourriez-vous raffraichir la page ?`
      );
    });
  }

  async wipeDatabase(): Promise<void[]> {
    return Promise.all([
      await this.drawings.clear(),
      await this.files.clear(),
      await this.projects.clear(),
      await this.backups.clear(),
      await this.drawingImages.clear(),
      await this.activityEvents.clear(),
      await this.planAnalyses.clear(),
    ]);
  }
}

export const db = new DRAppDatabase();

/**
 * Ouvre la base explicitement au démarrage. Sans cet appel, Dexie n'ouvre la
 * base qu'à la première requête et chaque échec se perd dans le `catch` de
 * l'appelant : l'app tourne alors sans aucun plan local ni sauvegarde
 * possible, et personne n'en sait rien — ni l'utilisateur, ni Sentry.
 *
 * Cas rencontrés :
 *  - `VersionError` : la base a été ouverte par un build plus récent (branche
 *    de dev avec `dbVersion` supérieur) — impossible à rouvrir ici ;
 *  - `UnknownError` / corruption : Chrome Android peut jeter la base d'une
 *    origine après un crash en pleine écriture ;
 *  - `QuotaExceededError` à l'ouverture sur un appareil saturé.
 */
export async function openDatabase(): Promise<void> {
  try {
    await db.open();
  } catch (e) {
    const error = e as { name?: string; message?: string };
    Logger.error(`database.ts : could not open IndexedDB — ${error?.message}`);
    Logger.captureException(e, {
      phase: "open",
      dbVersion: db.verno,
      errorName: error?.name,
    });
    const isVersionError = error?.name === "VersionError";
    alert(
      i18next.t(
        isVersionError
          ? "common:database.versionError"
          : "common:database.openError",
        { error: error?.name ?? "" }
      )
    );
  }
}
